import React from "react";
import { X, Eye, CalendarClock, Receipt, MessageSquare, Mic, CheckCircle2 } from "lucide-react";
import { useNavigationHistory } from "../../contexts/NavigationHistoryContext";

const HERO_IMAGE = "https://i.imgur.com/lAqoQiw.png";

// Grouped, plain-English rundown of every command Guru Voice understands —
// mirrors the intents defined in backend/services/voice_llm.py.
const CATEGORIES = [
  {
    key: "info",
    title: "Get Information",
    hint: "Answers right away — nothing to confirm.",
    icon: Eye,
    items: [
      { say: "\u201CShow my tasks\u201D", does: "Opens your open tasks." },
      { say: "\u201COpen messages\u201D", does: "Opens your team chat inbox." },
      { say: "\u201CRead my messages\u201D", does: "Reads your unread team messages back to you." },
      { say: "\u201CWhat appointments do I have today?\u201D", does: "Shows your scheduled jobs for that day." },
      { say: "\u201CWhat times are available on Friday?\u201D", does: "Lists the open time slots that day." },
      { say: "\u201CShow overdue jobs\u201D", does: "Lists jobs that are running behind schedule." },
      { say: "\u201CShow my follow-ups\u201D", does: "Lists the leads assigned to you to follow up on." },
      { say: "\u201CShow unpaid invoices\u201D", does: "Lists invoices that still have a balance due." },
      { say: "\u201CWhat's today's revenue?\u201D", does: "Summarizes today's booked value vs. money collected." },
      { say: "\u201CWho is available right now?\u201D", does: "Shows your teammates and their live status." },
      { say: "\u201CGet directions to my next job\u201D", does: "Opens Google Maps to your next stop." },
      { say: "\u201CCall Jane Smith\u201D", does: "Starts a call to that customer." },
      { say: "\u201CSend a message to Jane Smith\u201D", does: "Opens a text to that customer, ready for you to type." },
    ],
  },
  {
    key: "schedule",
    title: "Schedule & Calendar",
    hint: "Guru reads the plan back — confirm to save it.",
    icon: CalendarClock,
    items: [
      { say: "\u201CCreate a task to call the supplier\u201D", does: "Adds a new task, optionally assigned to a teammate." },
      { say: "\u201CCreate an event tomorrow at 3pm called team meeting\u201D", does: "Adds it to your personal calendar." },
      { say: "\u201CSchedule a job for Jane Smith on Friday at 10am\u201D", does: "Books a new job at the customer's address on file." },
      { say: "\u201CBook an appointment for Jane Smith on Monday at 2pm\u201D", does: "Adds it to the company calendar." },
      { say: "\u201CCancel Jane Smith's appointment on Monday\u201D", does: "Cancels that booking." },
      { say: "\u201CReschedule Jane Smith from Monday to Tuesday at 9am\u201D", does: "Moves the appointment to the new time." },
    ],
  },
  {
    key: "money",
    title: "Estimates, Invoices & Payments",
    hint: "Guru reads the plan back — confirm to save it.",
    icon: Receipt,
    items: [
      { say: "\u201CCreate an estimate for Jane Smith\u201D", does: "Starts a blank draft estimate for that customer." },
      { say: "\u201CAdd 2 hours of labor and a filter to the estimate\u201D", does: "Adds priced line items from your pricebook." },
      { say: "\u201CEmail the estimate to the customer\u201D", does: "Sends it with a link to view and sign." },
      { say: "\u201CText the estimate to the customer\u201D", does: "Sends that same link by SMS." },
      { say: "\u201CEmail the invoice\u201D or \u201CText the invoice\u201D", does: "Sends an existing invoice with a pay link." },
      { say: "\u201CMark this job complete\u201D", does: "Updates the job's status (also works for \u201Con my way\u201D, \u201Con site\u201D, etc.)." },
      { say: "\u201CRecord a $200 card payment\u201D", does: "Logs that payment against the invoice." },
    ],
  },
  {
    key: "notes",
    title: "Notes & Team",
    hint: "Guru reads the plan back — confirm to save it.",
    icon: MessageSquare,
    items: [
      { say: "\u201CAdd a note to this job: replaced the capacitor\u201D", does: "Logs a note on the job." },
      { say: "\u201CAdd a note for Jane Smith: wants a follow-up call\u201D", does: "Saves a note on the customer's file." },
      { say: "\u201CSend a message to Alex: running 15 minutes late\u201D", does: "Sends an internal chat message to that teammate." },
      { say: "\u201CText Jane Smith saying we're on our way\u201D", does: "Sends the customer that exact text." },
    ],
  },
];

export default function MobileVoiceCommands() {
  const { goBack } = useNavigationHistory();
  const close = () => goBack("/mobile");

  return (
    <div className="fixed inset-0 z-[70] bg-slate-50 flex flex-col" data-testid="mobile-voice-commands-page">
      <header
        className="shrink-0 flex items-center justify-between px-4 h-14 bg-white border-b border-slate-200"
        style={{ paddingTop: "env(safe-area-inset-top)", height: "calc(3.5rem + env(safe-area-inset-top))" }}
      >
        <span className="text-[17px] font-bold text-slate-900">Voice Commands</span>
        <button
          onClick={close}
          data-testid="mobile-voice-commands-close-btn"
          aria-label="Close"
          className="h-9 w-9 -mr-1.5 flex items-center justify-center rounded-full active:bg-slate-100"
        >
          <X className="h-5 w-5 text-slate-700" strokeWidth={2.2} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto" style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}>
        <img
          src={HERO_IMAGE}
          alt="Guru Voice Commands — tap to talk"
          className="w-full object-cover"
          data-testid="mobile-voice-commands-hero-image"
        />

        <div className="px-4 pt-5 space-y-1.5" data-testid="mobile-voice-commands-intro">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-blue-700">
            <Mic className="h-4 w-4" /> How it works
          </div>
          <p className="text-[13.5px] text-slate-600 leading-relaxed">
            Tap the mic button, then just talk — no special wording needed. Guru listens, and
            stops on its own after a few seconds of silence. For anything that changes data
            (like creating an estimate or sending a message), Guru reads the plan back first —
            tap <span className="font-semibold text-emerald-700">Confirm</span> to go ahead, or{" "}
            <span className="font-semibold text-slate-700">Redo</span> to try again.
          </p>
        </div>

        <div className="px-4 pt-6 space-y-6" data-testid="mobile-voice-commands-list">
          {CATEGORIES.map((cat) => (
            <div key={cat.key} data-testid={`mobile-voice-commands-category-${cat.key}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <cat.icon className="h-4 w-4 text-blue-600" />
                <span className="text-[15px] font-bold text-slate-900">{cat.title}</span>
              </div>
              <div className="text-[12px] text-slate-500 mb-2.5">{cat.hint}</div>
              <div className="space-y-2">
                {cat.items.map((item, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-white border border-slate-200 p-3"
                    data-testid={`mobile-voice-commands-item-${cat.key}-${i}`}
                  >
                    <div className="text-[13.5px] font-semibold text-slate-900 italic">{item.say}</div>
                    <div className="mt-1 flex items-start gap-1.5 text-[12.5px] text-slate-600 leading-snug">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{item.does}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-4 pt-6">
          <p className="text-[12px] text-slate-400 leading-relaxed">
            Tip: you can chain commands together in one breath, e.g. &ldquo;Mark this job complete
            and text the customer their invoice.&rdquo;
          </p>
        </div>
      </div>
    </div>
  );
}
